<script setup lang="ts">
import { computed, ref } from "vue";
import { Dialog } from "../../../../base-components/Headless";
import Button from "../../../../base-components/Button";
import { FormLabel, FormTextarea } from "../../../../base-components/Form";
import { numeroFormateado } from "../../../../utils/helper";
import type { Producto } from "@fixeduy/restaurantestypes";

interface Props {
  producto: Producto;
  imagenUrl?: string | null;
  cargando?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "cerrar"): void;
  (e: "agregar", payload: { cantidad: number; observaciones: string }): void;
}>();

const cantidad = ref(1);
const observaciones = ref("");

const subtotal = computed(() =>
  numeroFormateado(props.producto.precioConIva * cantidad.value, 2)
);

/** Texto informativo del catálogo (no confundir con las notas del ítem al agregar). */
const observacionesProductoInfo = computed(() => {
  const raw = props.producto.observaciones;
  if (raw == null) return "";
  const t = String(raw).trim();
  return t;
});

const incrementar = () => {
  cantidad.value += 1;
};

const decrementar = () => {
  if (cantidad.value <= 1) return;
  cantidad.value -= 1;
};

const agregar = () => {
  if (props.cargando) return;
  emit("agregar", {
    cantidad: cantidad.value,
    observaciones: observaciones.value.trim()
  });
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="lg">
    <Dialog.Panel class="overflow-hidden flex flex-col max-h-[85vh]">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          {{ props.producto.nombre }}
        </h2>
      </Dialog.Title>

      <Dialog.Description>
        <div class="flex flex-col gap-4">
          <div class="flex items-start gap-4">
            <div
              class="h-32 w-32 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 flex items-center justify-center"
            >
              <img
                :src="imagenUrl ?? '/productoSinImagen.png'"
                :alt="props.producto.nombre"
                class="h-full w-full object-cover"
              />
            </div>

            <div class="flex-1 min-w-0">
              <div class="text-2xl font-semibold text-primary">
                ${{ numeroFormateado(props.producto.precioConIva, 2) }}
              </div>

              <div
                v-if="observacionesProductoInfo"
                class="mt-2 text-xs text-slate-600 leading-snug"
              >
                <span class="font-medium text-slate-500">Observaciones</span>
                <p
                  class="mt-1 whitespace-pre-wrap break-words rounded-md border border-slate-200/80 bg-slate-50 px-2.5 py-2 text-sm text-slate-700"
                  role="note"
                >
                  {{ observacionesProductoInfo }}
                </p>
              </div>

              <div class="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  class="h-9 w-9 rounded-full bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200"
                  @click="decrementar"
                >
                  -
                </button>
                <div class="min-w-10 text-center text-xl font-semibold">
                  {{ cantidad }}
                </div>
                <button
                  type="button"
                  class="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 font-semibold hover:bg-emerald-200"
                  @click="incrementar"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div>
            <FormLabel class="mb-2 block">Notas</FormLabel>
            <FormTextarea
              v-model="observaciones"
              rows="5"
              maxlength="200"
              placeholder="Agregar una nota..."
              class="min-h-[120px]"
            />
            <div class="mt-1 text-right text-[11px] text-slate-500">
              {{ observaciones.length }}/200
            </div>
          </div>
        </div>
      </Dialog.Description>

      <Dialog.Footer>
        <div class="flex w-full gap-3">
          <div class="flex-1">
            <Button
              variant="secondary"
              class="w-full"
              @click="() => emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <div class="flex-1">
            <Button variant="primary" class="w-full" @click="agregar">
              <span
                v-if="props.cargando"
                class="inline-flex items-center justify-center gap-2"
              >
                <span
                  class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-transparent"
                />
                Agregando...
              </span>
              <span v-else>Agregar al pedido</span>
            </Button>
          </div>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>

